import { useQuery } from '@tanstack/react-query';
import { decodeOnSelect, queryTaskAsPromise } from '../../../../../../Appy';
import { useLearningCollection } from '../LearningCollectionContext';
import { sectionTypeOptionsDecoder } from '../Decoders/SectionTypeOptionsDecoder';
import { SectionTypeOptionsResource } from '../Types/SectionTemplates';

/**
 * Fetches section type options from the API, including defaults for Browse All sections
 * and available curated learning design templates.
 *
 * The hook retrieves template configuration data used when creating new sections:
 * - Browse All template: includes all available programs with default name, description, and display type
 * - Curated templates: array of pre-configured learning designs with themed program collections
 *
 * Results are cached for 15 minutes (staleTime: 15 * 60 * 1000) to balance fresh data
 * with performance, as curated templates may be updated periodically.
 *
 * @returns An object containing:
 *   - sectionTypeOptionsState: React Query state with section type options data
 *
 * @example
 * const { sectionTypeOptionsState } = useSectionTypeOptions();
 *
 * if (sectionTypeOptionsState.status === 'success') {
 *   const browseAllDefaults = sectionTypeOptionsState.data.payload.browse_all;
 *   const curatedSections = sectionTypeOptionsState.data.payload.curated;
 *   // Use browseAllDefaults.name, description, display_type, experiences
 *   // Use curatedSections to display available curated templates
 * }
 */
export const useSectionTypeOptions = () => {
  const { resourceState } = useLearningCollection();

  // Use first organization license ID for cache key uniqueness across orgs
  // This ensures curated sections data is correctly cached per-organization
  const orgLicenseId = resourceState.payload.organization_licenses[0]?.id;

  const sectionTypeOptionsState = useQuery<string, Error, SectionTypeOptionsResource | undefined>({
    queryKey: ['section-type-options', orgLicenseId],
    queryFn: () => queryTaskAsPromise('section-type-options', resourceState.links),
    select: decodeOnSelect(sectionTypeOptionsDecoder),
    enabled: resourceState.links.length > 0,
    staleTime: 15 * 60 * 1000, // 15 minutes
  });

  return {
    sectionTypeOptionsState,
  };
};
